import { DiscountGroup, MgItemPackage, MgItemPackages, SalesPackageType } from '..';
import { MgSupplierItemCardDTO } from '../../dto';
import { ProductGroupBase } from './ProductGroup';
import { MgSupplier } from './MgSupplier';
import { MgSupplierItemPackage } from './MgSupplierItemPackage';

export type ExternalSource = 'NOBB' | 'FINFO';

export const salesPackagePriority: Record<SalesPackageType, number> = {
  PRIMARY: 1,
  SECONDARY: 2,
  TERTIARY: 3,
};

interface Props {
  primaryText: string;
  secondaryText: string;

  mgSupplierItemId: number;
  externalId: string;
  externalSource: ExternalSource;
  prevMgItemNumber?: number;
  replacedBySupplierItemId?: string;
  supplierItemId: string;
  externalSupplierId: number;

  mgSupplierItemPackages: MgSupplierItemPackage[];

  registrationDate: string;
  discontinuationCode?: number;
  discontinuationDate?: string;
  createdAt: string;
  updatedAt: string;

  mgSupplier: MgSupplier;
  discountGroup?: DiscountGroup;
  productGroup: ProductGroupBase;

  basePrice: number;
  basePriceDate: string;
  basePriceUnit: string;
  vatCode?: string;
  currency?: string;

  webProductName?: string;
  textIngress?: string;
  textFull?: string;
  technicalDescription?: string;
  aiDescription?: string;
  youtubeInstallation?: string;

  modelNumber?: string;
  normalLeadTime?: number;
  source?: string;
  enumber?: number;
  originCountry?: string;
  dangerousGoods?: string;
  fscPefcClaim?: string;
  commodityCode?: string;
  articleType?: number;
  supplierProductName?: string;
}

export class MgSupplierItemCard {
  primaryText: string;
  secondaryText: string;

  mgSupplierItemId: number;
  externalId: string;
  externalSource: ExternalSource;
  prevMgItemNumber?: number;
  replacedBySupplierItemId?: string;
  supplierItemId: string;
  externalSupplierId: number;

  mgSupplierItemPackages: MgSupplierItemPackage[];

  registrationDate: string;
  discontinuationCode?: number;
  discontinuationDate?: string;
  createdAt: string;
  updatedAt: string;

  mgSupplier: MgSupplier;
  discountGroup?: DiscountGroup;
  productGroup: ProductGroupBase;

  basePrice: number;
  basePriceDate: string;
  basePriceUnit: string;
  vatCode?: string;
  currency?: string;

  webProductName?: string;
  textIngress?: string;
  textFull?: string;
  technicalDescription?: string;
  aiDescription?: string;
  youtubeInstallation?: string;

  modelNumber?: string;
  normalLeadTime?: number;
  source?: string;
  enumber?: number;
  originCountry?: string;
  dangerousGoods?: string;
  fscPefcClaim?: string;
  commodityCode?: string;
  articleType?: number;
  supplierProductName?: string;

  constructor({
    primaryText,
    secondaryText,

    mgSupplierItemId,
    externalId,
    externalSource,
    prevMgItemNumber,
    replacedBySupplierItemId,
    supplierItemId,
    externalSupplierId,

    mgSupplierItemPackages,

    registrationDate,
    discontinuationCode,
    discontinuationDate,
    createdAt,
    updatedAt,

    mgSupplier,
    discountGroup,
    productGroup,

    basePrice,
    basePriceDate,
    basePriceUnit,
    vatCode,
    currency,

    webProductName,
    textIngress,
    textFull,
    technicalDescription,
    aiDescription,
    youtubeInstallation,

    modelNumber,
    normalLeadTime,
    source,
    enumber,
    originCountry,
    dangerousGoods,
    fscPefcClaim,
    commodityCode,
    articleType,
    supplierProductName,
  }: Props) {
    this.primaryText = primaryText;
    this.secondaryText = secondaryText;

    this.mgSupplierItemId = mgSupplierItemId;
    this.externalId = externalId;
    this.externalSource = externalSource;
    this.prevMgItemNumber = prevMgItemNumber;
    this.replacedBySupplierItemId = replacedBySupplierItemId;
    this.supplierItemId = supplierItemId;
    this.externalSupplierId = externalSupplierId;

    this.mgSupplierItemPackages = mgSupplierItemPackages;

    this.registrationDate = registrationDate;
    this.discontinuationCode = discontinuationCode;
    this.discontinuationDate = discontinuationDate;
    this.createdAt = createdAt;
    this.updatedAt = updatedAt;

    this.mgSupplier = mgSupplier;
    this.discountGroup = discountGroup;
    this.productGroup = productGroup;

    this.basePrice = basePrice;
    this.basePriceDate = basePriceDate;
    this.basePriceUnit = basePriceUnit;
    this.vatCode = vatCode;
    this.currency = currency;

    this.webProductName = webProductName;
    this.textIngress = textIngress;
    this.textFull = textFull;
    this.technicalDescription = technicalDescription;
    this.aiDescription = aiDescription;
    this.youtubeInstallation = youtubeInstallation;

    this.modelNumber = modelNumber;
    this.normalLeadTime = normalLeadTime;
    this.source = source;
    this.enumber = enumber;
    this.originCountry = originCountry;
    this.dangerousGoods = dangerousGoods;
    this.fscPefcClaim = fscPefcClaim;
    this.commodityCode = commodityCode;
    this.articleType = articleType;
    this.supplierProductName = supplierProductName;
  }

  getSalesPackagesOrdered = () =>
    this.mgSupplierItemPackages
      .filter((p) => p.isSalesPackage())
      .sort(
        (p1, p2) =>
          salesPackagePriority[p1.type as SalesPackageType] -
          salesPackagePriority[p2.type as SalesPackageType]
      );

  static getPackages = (packages: MgSupplierItemPackage[]): MgItemPackages => {
    // @ts-ignore
    const uniquePackages: MgItemPackages = {};

    for (const pkg of packages) {
      const packageType = pkg.type;

      if (packageType) {
        if (!uniquePackages[packageType]) uniquePackages[packageType] = [];

        const newPackage: MgItemPackage = {
          gtinCode: pkg.gtinCode ?? '',
          unit: pkg.unit,
          isBasePriceUnit: pkg.basePriceUnit ?? false,
          calculatedCount: pkg.calculatedCount,
          packageType: pkg.type,
          classCode: pkg.classCode,
        };

        // Check if the package already exists in the array
        const packageExists = uniquePackages[packageType].some(
          (existingPackage) => existingPackage.gtinCode === newPackage.gtinCode
        );

        if (!packageExists) uniquePackages[packageType].push(newPackage);
      }
    }

    return uniquePackages;
  };

  static fromDto = (dto: MgSupplierItemCardDTO): MgSupplierItemCard =>
    new MgSupplierItemCard({
      primaryText: dto.primaryText,
      secondaryText: dto.secondaryText,

      mgSupplierItemId: dto.mgSupplierItemId,
      externalId: dto.externalId,
      externalSource: dto.externalSource,
      prevMgItemNumber: dto.prevMgItemNumber,
      replacedBySupplierItemId: dto.replacedBySupplierItemId,
      supplierItemId: dto.supplierItemId,
      externalSupplierId: dto.externalSupplierId,

      mgSupplierItemPackages: dto.mgSupplierItemPackages.map(MgSupplierItemPackage.fromDto),

      registrationDate: dto.registrationDate,
      discontinuationCode: dto.discontinuationCode,
      discontinuationDate: dto.discontinuationDate,
      createdAt: dto.createdAt,
      updatedAt: dto.updatedAt,

      mgSupplier: MgSupplier.fromDto(dto.mgSupplier),
      discountGroup: dto.discountGroup ? DiscountGroup.fromDto(dto.discountGroup) : undefined,
      productGroup: ProductGroupBase.fromDto(dto.productGroup),

      basePrice: dto.basePrice,
      basePriceDate: dto.basePriceDate,
      basePriceUnit: dto.basePriceUnit,
      vatCode: dto.vatCode,
      currency: dto.currency,

      webProductName: dto.webProductName,
      textIngress: dto.textIngress,
      textFull: dto.textFull,
      technicalDescription: dto.technicalDescription,
      aiDescription: dto.aiDescription,
      youtubeInstallation: dto.youtubeInstallation,

      modelNumber: dto.modelNumber,
      normalLeadTime: dto.normalLeadTime,
      source: dto.source,
      enumber: dto.enumber,
      originCountry: dto.originCountry,
      dangerousGoods: dto.dangerousGoods,
      fscPefcClaim: dto.fscPefcClaim,
      commodityCode: dto.commodityCode,
      articleType: dto.articleType,
      supplierProductName: dto.supplierProductName,
    });
}
